import { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { tryoutAPI } from '@/lib/api';
import { formatTime, cn } from '@/lib/utils';
import { sanitizeHTML } from '@/lib/sanitize';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Clock, ChevronLeft, ChevronRight, Flag, Send,
  CheckCircle, Circle, AlertTriangle, BookOpen
} from 'lucide-react';

// Memoized component to prevent re-rendering of question content (fixes flickering)
const QuestionContent = memo(function QuestionContent({ html }) {
  const sanitizedHtml = useMemo(() => sanitizeHTML(html), [html]);
  return (
    <div 
      className="text-lg text-slate-900 leading-relaxed prose prose-slate max-w-none question-content" 
      data-testid="question-text"
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
});

// Memoized option content
const OptionContent = memo(function OptionContent({ html }) {
  const sanitizedHtml = useMemo(() => sanitizeHTML(html), [html]);
  return <span className="text-slate-700 option-text" dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
});

export default function ExamPage() {
  const { tryoutId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [tryout, setTryout] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [timeLimits, setTimeLimits] = useState([]); // Per-mapel time limits
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState(new Set());
  const [timeLeft, setTimeLeft] = useState(0);
  const [mapelTimers, setMapelTimers] = useState({}); // Per-mapel remaining times
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showTimeWarning, setShowTimeWarning] = useState(false);
  const [warningShown, setWarningShown] = useState(new Set()); // Track which mapel warnings shown
  const startTimeRef = useRef(Date.now());

  // AUTO-SAVE: Key for localStorage
  const storageKey = `exam_${tryoutId}_answers`;
  const storageKeyFlags = `exam_${tryoutId}_flagged`;

  // AUTO-SAVE: Save answers to localStorage whenever they change
  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      localStorage.setItem(storageKey, JSON.stringify(answers));
    }
  }, [answers, storageKey]);

  // AUTO-SAVE: Save flagged questions
  useEffect(() => {
    if (flagged.size > 0) {
      localStorage.setItem(storageKeyFlags, JSON.stringify([...flagged]));
    }
  }, [flagged, storageKeyFlags]);

  // AUTO-SAVE: Load saved answers on mount
  useEffect(() => {
    try {
      const savedAnswers = localStorage.getItem(storageKey);
      const savedFlagged = localStorage.getItem(storageKeyFlags);
      if (savedAnswers) {
        setAnswers(JSON.parse(savedAnswers));
      }
      if (savedFlagged) {
        setFlagged(new Set(JSON.parse(savedFlagged)));
      }
    } catch (e) {
      console.warn('Failed to load saved exam progress:', e);
    }
  }, [storageKey, storageKeyFlags]);

  // AUTO-SAVE: Clear localStorage after successful submit
  const clearSavedProgress = useCallback(() => {
    localStorage.removeItem(storageKey);
    localStorage.removeItem(storageKeyFlags);
  }, [storageKey, storageKeyFlags]);

  // Group questions by mapel
  const questionsByMapel = useMemo(() => {
    const groups = {};
    questions.forEach((q, index) => {
      const mapel = q.mapel || 'Umum';
      if (!groups[mapel]) {
        groups[mapel] = [];
      }
      groups[mapel].push({ ...q, originalIndex: index });
    });
    return groups;
  }, [questions]);

  // Get current question's mapel
  const currentQuestion = questions[currentIndex];
  const currentMapel = currentQuestion?.mapel || 'Umum';

  // Check if we have per-mapel time limits
  const hasMapelTimeLimits = timeLimits.length > 0;

  // Warning 1 minute before time ends
  useEffect(() => {
    const currentTime = hasMapelTimeLimits ? (mapelTimers[currentMapel] || 0) : timeLeft;
    const warningKey = hasMapelTimeLimits ? currentMapel : 'total';
    
    // Show warning when time reaches 60 seconds (1 minute)
    if (currentTime === 60 && !warningShown.has(warningKey)) {
      setShowTimeWarning(true);
      setWarningShown(prev => new Set([...prev, warningKey]));
      
      // Auto-hide warning after 5 seconds
      setTimeout(() => setShowTimeWarning(false), 5000);
    }
  }, [timeLeft, mapelTimers, currentMapel, hasMapelTimeLimits, warningShown]);

  useEffect(() => {
    loadExam();
  }, [tryoutId]);

  useEffect(() => {
    if (timeLeft <= 0 && !hasMapelTimeLimits) return;
    if (hasMapelTimeLimits && Object.values(mapelTimers).every(t => t <= 0)) {
      handleSubmit();
      return;
    }
    
    const timer = setInterval(() => {
      if (hasMapelTimeLimits) {
        // Decrement current mapel timer
        setMapelTimers((prev) => {
          const newTimers = { ...prev };
          if (newTimers[currentMapel] > 0) {
            newTimers[currentMapel] -= 1;
            // Auto-move to next mapel section if time is up
            if (newTimers[currentMapel] <= 0) {
              const mapels = Object.keys(questionsByMapel);
              const currentMapelIdx = mapels.indexOf(currentMapel);
              const nextMapelIdx = currentMapelIdx + 1;
              if (nextMapelIdx < mapels.length) {
                const nextMapel = mapels[nextMapelIdx];
                const firstQOfNextMapel = questionsByMapel[nextMapel][0];
                if (firstQOfNextMapel) {
                  setTimeout(() => setCurrentIndex(firstQOfNextMapel.originalIndex), 100);
                }
              }
            }
          }
          return newTimers;
        });
      } else {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, hasMapelTimeLimits, mapelTimers, currentMapel, questionsByMapel]);

  const loadExam = async () => {
    try {
      const res = await tryoutAPI.getQuestions(tryoutId);
      setTryout(res.data.tryout);
      setQuestions(res.data.questions);
      const fetchedTimeLimits = res.data.time_limits || res.data.tryout.time_limits || [];
      setTimeLimits(fetchedTimeLimits);
      
      if (fetchedTimeLimits.length > 0) {
        // Initialize per-mapel timers
        const initialTimers = {};
        fetchedTimeLimits.forEach(tl => {
          initialTimers[tl.mapel] = tl.duration_minutes * 60;
        });
        setMapelTimers(initialTimers);
        // Set total time as sum of all mapel times
        const totalSeconds = fetchedTimeLimits.reduce((sum, tl) => sum + tl.duration_minutes * 60, 0);
        setTimeLeft(totalSeconds);
      } else {
        setTimeLeft(res.data.tryout.duration_minutes * 60);
      }
    } catch (err) {
      console.error('Failed to load exam:', err);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAnswer = (questionId, optionId) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: optionId
    }));
  };

  const handleToggleFlag = (questionId) => {
    setFlagged((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
    
    const submission = {
      tryout_id: tryoutId,
      answers: Object.entries(answers).map(([questionId, optionId]) => ({
        question_id: questionId,
        selected_option_id: optionId
      })),
      time_spent_seconds: timeSpent
    };

    try {
      const res = await tryoutAPI.submit(tryoutId, submission);
      // Clear saved progress after successful submit
      clearSavedProgress();
      navigate(`/result/${res.data.id}`);
    } catch (err) {
      console.error('Failed to submit:', err);
      setSubmitting(false);
    }
  };

  const answeredCount = Object.keys(answers).length;
  const progress = (answeredCount / questions.length) * 100;

  // Get current mapel timer or total timer
  const getCurrentTimer = () => {
    if (hasMapelTimeLimits && currentMapel) {
      return mapelTimers[currentMapel] || 0;
    }
    return timeLeft;
  };

  const getTimeColor = (time = getCurrentTimer()) => {
    if (time <= 60) return 'text-red-600 bg-red-100 animate-pulse border-2 border-red-400';
    if (time <= 300) return 'text-orange-600 bg-orange-50 border border-orange-300';
    return 'text-blue-600 bg-blue-50 border border-blue-200';
  };

  // Check if current mapel time is up
  const isCurrentMapelTimeUp = hasMapelTimeLimits && (mapelTimers[currentMapel] || 0) <= 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0047AB] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-slate-500">Memuat soal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="exam-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-[#0047AB] rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-semibold text-slate-900">{tryout?.title}</h1>
                <p className="text-sm text-slate-500">{tryout?.category}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              {/* Timer - Per Mapel or Total - BIGGER and BOLDER */}
              {hasMapelTimeLimits ? (
                <div className="flex items-center gap-2">
                  <div className={cn("px-4 py-3 rounded-xl font-mono text-xl font-bold shadow-md", getTimeColor(getCurrentTimer()))} data-testid="exam-timer">
                    <Clock className="w-5 h-5 inline mr-2" />
                    <span className="text-sm font-semibold mr-2">{currentMapel}:</span>
                    <span className="text-2xl">{formatTime(getCurrentTimer())}</span>
                  </div>
                </div>
              ) : (
                <div className={cn("px-5 py-3 rounded-xl font-mono text-2xl font-bold shadow-md", getTimeColor())} data-testid="exam-timer">
                  <Clock className="w-6 h-6 inline mr-2" />
                  <span className="text-3xl">{formatTime(timeLeft)}</span>
                </div>
              )}
              
              {/* Progress */}
              <div className="hidden md:block w-48">
                <div className="text-sm text-slate-500 mb-1">
                  {answeredCount}/{questions.length} Terjawab
                </div>
                <Progress value={progress} className="h-2" />
              </div>
              
              {/* Submit Button */}
              <Button 
                onClick={() => setShowSubmitDialog(true)}
                className="bg-[#CCFF00] text-black hover:bg-[#CCFF00]/90 font-bold"
                data-testid="submit-exam-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Selesai
              </Button>
            </div>
          </div>
          
          {/* Mapel Time Limits Bar */}
          {hasMapelTimeLimits && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
              {timeLimits.map((tl) => (
                <Badge 
                  key={tl.mapel} 
                  variant={currentMapel === tl.mapel ? "default" : "outline"}
                  className={cn(
                    "text-sm font-bold px-3 py-1",
                    currentMapel === tl.mapel && "bg-blue-600 text-white shadow-md",
                    (mapelTimers[tl.mapel] || 0) <= 60 && (mapelTimers[tl.mapel] || 0) > 0 && "bg-red-500 text-white animate-pulse",
                    (mapelTimers[tl.mapel] || 0) <= 0 && "opacity-50 line-through bg-slate-300"
                  )}
                >
                  {tl.mapel}: {formatTime(mapelTimers[tl.mapel] || 0)}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Question Panel */}
          <div className="lg:col-span-3">
            <Card className="shadow-lg">
              <CardContent className="p-8">
                {/* Mapel Time Up Warning */}
                {isCurrentMapelTimeUp && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-5 h-5" />
                    <span>Waktu untuk {currentMapel} sudah habis! Silakan pindah ke bagian lain atau submit jawaban.</span>
                  </div>
                )}
                
                {/* Question Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-base px-4 py-1">
                      Soal {currentIndex + 1} dari {questions.length}
                    </Badge>
                    {currentQuestion?.mapel && (
                      <Badge variant="outline" className="text-sm px-3 py-1">
                        {currentQuestion.mapel}
                      </Badge>
                    )}
                  </div>
                  <Button
                    variant={flagged.has(currentQuestion?.id) ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleToggleFlag(currentQuestion?.id)}
                    className={flagged.has(currentQuestion?.id) ? "bg-orange-500 hover:bg-orange-600" : ""}
                    data-testid="flag-question-btn"
                  >
                    <Flag className="w-4 h-4 mr-1" />
                    {flagged.has(currentQuestion?.id) ? 'Ditandai' : 'Tandai'}
                  </Button>
                </div>

                {/* Question Text */}
                <div className="mb-8">
                  <QuestionContent html={currentQuestion?.text} />
                </div>

                {/* Options */}
                <div className="space-y-3">
                  {currentQuestion?.options.map((option, i) => (
                    <button
                      key={option.id}
                      onClick={() => !isCurrentMapelTimeUp && handleSelectAnswer(currentQuestion.id, option.id)}
                      disabled={isCurrentMapelTimeUp}
                      className={cn(
                        "w-full text-left p-4 rounded-xl border-2 transition-all",
                        answers[currentQuestion?.id] === option.id
                          ? "border-[#0047AB] bg-[#0047AB]/5"
                          : "border-slate-200 hover:border-slate-300 hover:bg-slate-50",
                        isCurrentMapelTimeUp && "opacity-50 cursor-not-allowed"
                      )}
                      data-testid={`option-${i}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm shrink-0",
                          answers[currentQuestion?.id] === option.id
                            ? "bg-[#0047AB] text-white"
                            : "bg-slate-100 text-slate-600"
                        )}>
                          {String.fromCharCode(65 + i)}
                        </div>
                        <OptionContent html={option.text} />
                      </div>
                    </button>
                  ))}
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between mt-8 pt-6 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
                    disabled={currentIndex === 0}
                    data-testid="prev-question-btn"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Sebelumnya
                  </Button>
                  <Button
                    onClick={() => setCurrentIndex((prev) => Math.min(questions.length - 1, prev + 1))}
                    disabled={currentIndex === questions.length - 1}
                    className="bg-[#0047AB] hover:bg-[#0047AB]/90"
                    data-testid="next-question-btn"
                  >
                    Selanjutnya
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Navigation Panel */}
          <div className="lg:col-span-1">
            <Card className="sticky top-28">
              <CardContent className="p-4">
                <h3 className="font-semibold text-slate-900 mb-4">Navigasi Soal</h3>
                <div className="grid grid-cols-5 gap-2">
                  {questions.map((q, i) => (
                    <button
                      key={q.id}
                      onClick={() => setCurrentIndex(i)}
                      className={cn(
                        "w-10 h-10 rounded-lg text-sm font-medium transition-all relative",
                        currentIndex === i
                          ? "bg-[#0047AB] text-white"
                          : answers[q.id]
                            ? "bg-green-100 text-green-700"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      )}
                      data-testid={`nav-question-${i}`}
                    >
                      {i + 1}
                      {flagged.has(q.id) && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full" />
                      )}
                    </button>
                  ))}
                </div>
                
                {/* Legend */}
                <div className="mt-6 space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-100 rounded" />
                    <span className="text-slate-600">Terjawab</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-slate-100 rounded" />
                    <span className="text-slate-600">Belum dijawab</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-slate-100 rounded relative">
                      <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-orange-500 rounded-full" />
                    </div>
                    <span className="text-slate-600">Ditandai</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Submit Confirmation Dialog */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Konfirmasi Selesai</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <span>Apakah Anda yakin ingin menyelesaikan ujian?</span>
                <div className="bg-slate-50 p-4 rounded-xl space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Terjawab:</span>
                    <span className="font-semibold text-green-600">{answeredCount} soal</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Belum dijawab:</span>
                    <span className="font-semibold text-red-600">{questions.length - answeredCount} soal</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Ditandai:</span>
                    <span className="font-semibold text-orange-600">{flagged.size} soal</span>
                  </div>
                </div>
                {questions.length - answeredCount > 0 && (
                  <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="text-sm">Masih ada soal yang belum dijawab!</span>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-submit-btn">Kembali</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#0047AB] hover:bg-[#0047AB]/90 relative z-50"
              data-testid="confirm-submit-btn"
            >
              {submitting ? 'Mengirim...' : 'Selesai & Kirim'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Time Warning Alert - 1 Minute Before End */}
      <AlertDialog open={showTimeWarning} onOpenChange={setShowTimeWarning}>
        <AlertDialogContent className="border-red-500 border-2">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 flex items-center gap-2 text-xl">
              <AlertTriangle className="w-6 h-6 animate-bounce" />
              PERINGATAN WAKTU!
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 p-4 rounded-xl text-center">
                  <p className="text-red-700 font-bold text-lg">
                    Waktu ujian tersisa <span className="text-2xl text-red-600">1 MENIT!</span>
                  </p>
                  <p className="text-red-600 mt-2">
                    {hasMapelTimeLimits ? `Mata Pelajaran: ${currentMapel}` : 'Segera selesaikan jawaban Anda!'}
                  </p>
                </div>
                <div className="bg-amber-50 p-3 rounded-lg">
                  <p className="text-amber-800 text-sm">
                    <strong>Tips:</strong> Pastikan semua soal sudah terjawab sebelum waktu habis. 
                    Jawaban akan otomatis dikumpulkan saat waktu berakhir.
                  </p>
                </div>
                <div className="text-center">
                  <span className="text-slate-600">Soal terjawab: </span>
                  <span className="font-bold text-green-600">{answeredCount}</span>
                  <span className="text-slate-600"> / {questions.length}</span>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction 
              onClick={() => setShowTimeWarning(false)}
              className="bg-red-600 hover:bg-red-700 text-white font-bold"
            >
              Mengerti, Lanjutkan!
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
